import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { ExpenseFormValues } from "./expense-schema";
import type { DiscountOption, TaxOption } from "./use-expense-form";
import type { UseFormReturn } from "react-hook-form";

interface ExpenseSummaryBoxProps {
  form: UseFormReturn<ExpenseFormValues>;
  itemsSubtotal: number;
  grandTotal: number;
  discountOption: DiscountOption;
  setDiscountOption: (option: DiscountOption) => void;
  selectDiscountOption: (option: DiscountOption) => void;
  taxOption: TaxOption;
  setTaxOption: (option: TaxOption) => void;
  selectTaxOption: (option: TaxOption) => void;
}

export function ExpenseSummaryBox({
  form,
  itemsSubtotal,
  grandTotal,
  discountOption,
  setDiscountOption,
  selectDiscountOption,
  taxOption,
  setTaxOption,
  selectTaxOption,
}: ExpenseSummaryBoxProps) {
  return (
    <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
      {/* Subtotal Item */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Subtotal Item:</span>
        <span className="font-semibold text-foreground">
          Rp {(itemsSubtotal || 0).toLocaleString("id-ID")}
        </span>
      </div>

      {/* Diskon & Pajak Inputs - Symmetric Layout */}
      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/60">
        <FormField
          control={form.control}
          name="discountAmount"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <div className="flex h-5 items-center justify-between">
                <FormLabel className="text-[11px] text-muted-foreground font-normal">
                  Diskon / Potongan
                </FormLabel>
                <Select
                  value={discountOption}
                  onValueChange={(val) =>
                    selectDiscountOption(val as DiscountOption)
                  }
                >
                  <SelectTrigger
                    size="sm"
                    className="h-5 w-auto text-[10px] px-1.5 py-0 border-none bg-muted/60 hover:bg-muted font-medium text-foreground gap-1 shadow-none"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="none" className="text-xs">
                      Tanpa Diskon (0%)
                    </SelectItem>
                    <SelectItem value="disc5" className="text-xs">
                      Diskon (5%)
                    </SelectItem>
                    <SelectItem value="disc10" className="text-xs">
                      Diskon (10%)
                    </SelectItem>
                    <SelectItem value="manual" className="text-xs">
                      Nominal Manual
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormControl>
                <div className="relative">
                  <span className="absolute left-2.5 top-1.5 text-xs text-muted-foreground select-none">
                    Rp
                  </span>
                  <NumberInput
                    name="discountAmount"
                    placeholder="0"
                    className="h-8 pl-8 text-right text-base font-medium sm:text-xs"
                    value={field.value ?? 0}
                    onChange={(val) => {
                      field.onChange(val || 0);
                      setDiscountOption("manual");
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="taxAmount"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <div className="flex h-5 items-center justify-between">
                <FormLabel className="text-[11px] text-muted-foreground font-normal">
                  Pajak
                </FormLabel>
                <Select
                  value={taxOption}
                  onValueChange={(val) => selectTaxOption(val as TaxOption)}
                >
                  <SelectTrigger
                    size="sm"
                    className="h-5 w-auto text-[10px] px-1.5 py-0 border-none bg-muted/60 hover:bg-muted font-medium text-foreground gap-1 shadow-none"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="end">
                    <SelectItem value="none" className="text-xs">
                      Tanpa Pajak (0%)
                    </SelectItem>
                    <SelectItem value="ppn11" className="text-xs">
                      PPN (11%)
                    </SelectItem>
                    <SelectItem value="pb1_10" className="text-xs">
                      PB1 Resto (10%)
                    </SelectItem>
                    <SelectItem value="manual" className="text-xs">
                      Nominal Manual
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormControl>
                <div className="relative">
                  <span className="absolute left-2.5 top-1.5 text-xs text-muted-foreground select-none">
                    Rp
                  </span>
                  <NumberInput
                    name="taxAmount"
                    placeholder="0"
                    className="h-8 pl-8 text-right text-base font-medium sm:text-xs"
                    value={field.value ?? 0}
                    onChange={(val) => {
                      field.onChange(val || 0);
                      setTaxOption("manual");
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Total Akhir / Kas Keluar */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <span className="text-xs font-semibold text-foreground">
          Total Kas Keluar:
        </span>
        <span className="text-base font-bold text-foreground">
          Rp {(grandTotal || 0).toLocaleString("id-ID")}
        </span>
      </div>
    </div>
  );
}
